package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.OptimizationQuotaStrings
import com.milta.feature.aicredit.domain.usecase.GetAccountAiCreditHistoryUseCase
import com.milta.feature.aicredit.domain.usecase.GetAccountAiCreditUseCase
import com.milta.feature.aicredit.domain.usecase.GetAiCreditSettingsUseCase
import com.milta.feature.aicredit.domain.usecase.ReplaceRemainingAiCreditUseCase
import com.milta.feature.aicredit.presentation.AdminAccountAiCreditIntent
import com.milta.feature.aicredit.presentation.AdminAccountAiCreditViewModel
import com.milta.feature.admin.domain.entity.AdminAccount
import com.milta.feature.optimizationquota.domain.entity.AdminAccountOptimizationQuota
import com.milta.feature.optimizationquota.domain.entity.AdminQuotaAccountKind
import com.milta.feature.optimizationquota.domain.entity.OptimizationAccountKind
import com.milta.feature.optimizationquota.domain.entity.OptimizationSubscriptionKind
import com.milta.feature.optimizationquota.domain.usecase.GetAccountOptimizationHistoryUseCase
import com.milta.feature.optimizationquota.domain.usecase.GetAccountOptimizationQuotaUseCase
import com.milta.feature.optimizationquota.domain.usecase.ReplaceRemainingOptimizationsUseCase
import com.milta.feature.optimizationquota.presentation.AdminAccountQuotaIntent
import com.milta.feature.optimizationquota.presentation.AdminAccountQuotaState
import com.milta.feature.optimizationquota.presentation.AdminAccountQuotaViewModel
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaFormat
import com.milta.feature.optimizationquota.presentation.fillPlaceholders
import com.milta.feature.optimizationquota.presentation.ratioLabel
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject

/**
 * MIL-97 — A2, Admin "Détails de l'utilisateur", opened from "Utilisateurs": identity, account kind,
 * subscription, the account's own counter "restant / limite" with the source of its limit and its
 * reset date (R1, R3), "Remplacer le nombre restant" for the current period only (R5) and the paged
 * history (R12, R13). [listLabel] is the breadcrumb's first part ("Utilisateurs").
 */
@Composable
fun AdminAccountQuotaContent(accountId: String, listLabel: String, onBack: () -> Unit) {
    val getAccountQuota: GetAccountOptimizationQuotaUseCase = koinInject()
    val getAccountHistory: GetAccountOptimizationHistoryUseCase = koinInject()
    val replaceRemaining: ReplaceRemainingOptimizationsUseCase = koinInject()
    val viewModel = remember(accountId) {
        AdminAccountQuotaViewModel(accountId, getAccountQuota, getAccountHistory, replaceRemaining)
    }
    DisposableEffect(viewModel) { onDispose { viewModel.clear() } }
    val state by viewModel.state.collectAsState()
    val strings = LocalStrings.current.optimizationQuota
    val data = state.data
    // MIL-98 — the account's AI credit, block "Crédit IA" (A2).
    val getAccountAiCredit: GetAccountAiCreditUseCase = koinInject()
    val getAccountAiCreditHistory: GetAccountAiCreditHistoryUseCase = koinInject()
    val replaceRemainingAiCredit: ReplaceRemainingAiCreditUseCase = koinInject()
    val getAiCreditSettings: GetAiCreditSettingsUseCase = koinInject()
    val aiViewModel = remember(accountId) {
        AdminAccountAiCreditViewModel(accountId, getAccountAiCredit, getAccountAiCreditHistory, replaceRemainingAiCredit, getAiCreditSettings)
    }
    DisposableEffect(aiViewModel) { onDispose { aiViewModel.clear() } }
    val aiState by aiViewModel.state.collectAsState()

    // Full width of the content area (mockup B3): identity band on top, then two equal columns,
    // "Optimisations RoutaR" | "Crédit IA", each with its history below. Each block keeps its own
    // loading / error / "Réessayer".
    Div(attrs = { style { property("width", "100%"); property("box-sizing", "border-box") } }) {
        Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary); property("margin-bottom", "14px") } }) {
            Span(attrs = {
                onClick { onBack() }
                style { property("cursor", "pointer"); property("color", WebPrimaryMid); property("font-weight", "600") }
            }) { Text("‹ $listLabel") }
            val account = data?.account ?: aiState.data?.account
            if (account != null) Text(" / ${account.firstName} ${account.lastName}")
        }
        when {
            state.isLoading && data == null && aiState.isLoading && aiState.data == null -> WebSpinner()
            // An unknown (or Admin) account is a 404 on both endpoints.
            state.error == "not_found" && data == null -> SheetLoadError(strings.accountNotFound, onRetry = null)
            else -> Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "18px") } }) {
                val aiData = aiState.data
                when {
                    data != null -> IdentityBand(
                        account = data.account,
                        accountKind = data.accountKind,
                        enterpriseName = data.enterpriseName,
                        subscriptionKind = data.quota.subscriptionKind,
                        resetAt = data.quota.resetAt,
                        strings = strings,
                    )
                    // The identity is also carried by the AI credit answer when the optimizations failed.
                    aiData != null -> IdentityBand(
                        account = aiData.account,
                        accountKind = aiData.accountKind,
                        enterpriseName = aiData.enterpriseName,
                        subscriptionKind = aiData.credit.subscriptionKind,
                        resetAt = aiData.credit.resetAt,
                        strings = strings,
                    )
                }
                Div(attrs = {
                    style {
                        property("display", "grid"); property("grid-template-columns", "minmax(0, 1fr) minmax(0, 1fr)")
                        property("gap", "18px"); property("align-items", "start")
                    }
                }) {
                    ColumnStack {
                        when {
                            data != null -> {
                                QuotaCard(data, state, strings, viewModel::onIntent)
                                AdminSheetCard(title = strings.adminHistoryTitle) {
                                    OptimizationHistoryTable(
                                        history = state.history,
                                        strings = strings,
                                        onLoadMore = { viewModel.onIntent(AdminAccountQuotaIntent.LoadMoreHistory) },
                                        maxHeightPx = 420,
                                    )
                                }
                            }
                            state.isLoading -> WebSpinner(size = 20, strokeWidth = 2)
                            else -> SheetLoadError(loadErrorText(state.error, strings), onRetry = { viewModel.onIntent(AdminAccountQuotaIntent.Load) }, retryLabel = strings.retryAction)
                        }
                    }
                    ColumnStack {
                        when {
                            aiData != null -> AdminAiCreditCard(aiData, aiState, aiViewModel::onIntent)
                            aiState.isLoading -> WebSpinner(size = 20, strokeWidth = 2)
                            else -> SheetLoadError(
                                loadErrorText(aiState.error, strings),
                                onRetry = { aiViewModel.onIntent(AdminAccountAiCreditIntent.Load) },
                                retryLabel = strings.retryAction,
                            )
                        }
                        // Its own paging state, shown even while the credit card failed.
                        AdminAiCreditHistoryCard(aiData, aiState, aiViewModel::onIntent)
                    }
                }
            }
        }
    }
}

@Composable
private fun ColumnStack(content: @Composable () -> Unit) {
    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "18px"); property("min-width", "0") } }) {
        content()
    }
}

private fun loadErrorText(error: String?, strings: OptimizationQuotaStrings) = when (error) {
    "not_found" -> strings.accountNotFound
    "offline" -> strings.errorNoConnection
    else -> strings.errorGeneric
}

/** A block of the sheet that could not be read, with "Réessayer" when it may work again. */
@Composable
internal fun SheetLoadError(message: String, onRetry: (() -> Unit)?, retryLabel: String? = null) {
    Div(attrs = {
        style {
            padding(16.px); property("background", WebErrorBg); property("color", WebErrorInk); property("border-radius", "10px")
            property("display", "flex"); property("align-items", "center"); property("gap", "12px"); property("flex-wrap", "wrap")
        }
    }) {
        Span { Text(message) }
        if (onRetry != null && retryLabel != null) {
            Button(attrs = { onClick { onRetry() }; style { secondaryButtonStyle(this) } }) { Text(retryLabel) }
        }
    }
}

@Composable
internal fun AdminSheetCard(title: String, content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            property("background", WebSurface); property("border", "1px solid $WebBorder")
            property("border-radius", "12px"); padding(18.px)
        }
    }) {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "8px"); property("margin-bottom", "14px") } }) {
            Div(attrs = {
                style {
                    property("font-size", "11px"); property("font-weight", "700"); property("letter-spacing", "0.06em")
                    property("text-transform", "uppercase"); property("color", WebTextSecondary)
                }
            }) { Text(title) }
        }
        content()
    }
}

/**
 * Identity band (mockup B3): avatar, name and contact, account kind, subscription and the reset
 * date shared by both counters.
 */
@Composable
private fun IdentityBand(
    account: AdminAccount,
    accountKind: AdminQuotaAccountKind,
    enterpriseName: String?,
    subscriptionKind: OptimizationSubscriptionKind,
    resetAt: String,
    strings: OptimizationQuotaStrings,
) {
    val name = "${account.firstName} ${account.lastName}".trim()
    AdminSheetCard(title = strings.sectionIdentity) {
        Div(attrs = {
            style {
                property("display", "grid"); property("grid-template-columns", "auto 1fr 1fr 1fr")
                property("gap", "24px"); property("align-items", "center")
            }
        }) {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "12px"); property("min-width", "0") } }) {
                Div(attrs = {
                    style {
                        property("width", "40px"); property("height", "40px"); property("border-radius", "50%")
                        property("background", avatarColorFor(initials(name)))
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                        property("color", "#FFFFFF"); property("font-weight", "700"); property("flex-shrink", "0")
                    }
                }) { Text(initials(name)) }
                Div {
                    Div(attrs = { style { property("font-weight", "700"); property("color", WebTextPrimary) } }) { Text(name) }
                    Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary) } }) {
                        Text(listOfNotNull(account.email, account.phoneNumber).joinToString(" · "))
                    }
                }
            }
            val kindLabel = when (accountKind) {
                AdminQuotaAccountKind.EnterpriseOwner -> strings.kindEnterpriseOwner
                AdminQuotaAccountKind.EnterpriseDriver -> strings.kindEnterpriseDriver
                AdminQuotaAccountKind.IndependentDriver -> strings.kindIndependentDriver
            }
            BandItem(strings.accountTypeLabel, kindLabel, enterpriseName ?: strings.withoutOrganisation)
            val subscription = subscriptionLabel(subscriptionKind, strings)
            if (accountKind == AdminQuotaAccountKind.EnterpriseDriver) {
                BandItem(strings.subscriptionLabel, strings.viaOrganisation, listOfNotNull(enterpriseName, subscription).joinToString(" : "))
            } else {
                BandItem(strings.subscriptionLabel, subscription, null)
            }
            val resetSource = if (accountKind == AdminQuotaAccountKind.EnterpriseDriver && enterpriseName != null) {
                strings.resetSourceEnterprise.fillPlaceholders("enterprise" to enterpriseName)
            } else {
                strings.resetSourceOwn
            }
            BandItem(strings.resetLabel, OptimizationQuotaFormat.fullDate(resetAt), resetSource)
        }
    }
}

@Composable
private fun BandItem(label: String, value: String, detail: String?) {
    Div(attrs = { style { property("min-width", "0") } }) {
        Div(attrs = {
            style {
                property("font-size", "11px"); property("color", WebTextSecondary); property("text-transform", "uppercase")
                property("letter-spacing", "0.05em"); property("font-weight", "700"); property("margin-bottom", "4px")
            }
        }) { Text(label) }
        Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) { Text(value) }
        if (detail != null) {
            Div(attrs = { style { property("font-size", "11.5px"); property("color", WebTextSecondary); property("margin-top", "3px") } }) { Text(detail) }
        }
    }
}

@Composable
private fun QuotaCard(
    data: AdminAccountOptimizationQuota,
    state: AdminAccountQuotaState,
    strings: OptimizationQuotaStrings,
    onIntent: (AdminAccountQuotaIntent) -> Unit,
) {
    val quota = data.quota
    AdminSheetCard(title = strings.tabOptimizations) {
        val replacement = state.lastReplacement
        if (replacement != null) {
            InlineBanner(
                kind = InlineBannerKind.Success,
                text = strings.replacedBanner.fillPlaceholders(
                    "from" to replacement.from,
                    "to" to replacement.to,
                    "date" to OptimizationQuotaFormat.fullDate(quota.resetAt),
                    "limit" to quota.globalLimit,
                ),
            )
        }
        Div(attrs = { style { property("display", "flex"); property("align-items", "baseline"); property("gap", "8px"); property("margin-bottom", "10px"); property("flex-wrap", "wrap") } }) {
            Div(attrs = {
                style { property("font-size", "26px"); property("font-weight", "800"); property("color", if (quota.isExhausted) WebErrorInk else WebPrimary) }
            }) { Text(quota.ratioLabel) }
            Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text(strings.remainingSuffix) }
            if (quota.isExhausted) AdminStatusChip(strings.exhaustedBadge, WebErrorInk, WebErrorBg)
            if (quota.isManuallyAdjusted) AdminStatusChip(strings.manuallyAdjustedBadge, WebPrimary, WebActiveTint)
        }
        val limitKind = if (quota.accountKind == OptimizationAccountKind.Enterprise) strings.limitKindEnterprise else strings.limitKindDriver
        val limitSource = buildString {
            append("$limitKind · ${subscriptionLabel(quota.subscriptionKind, strings).lowercase()}")
            if (data.accountKind == AdminQuotaAccountKind.EnterpriseDriver && quota.subscriptionKind == OptimizationSubscriptionKind.Trial) {
                append(" ${strings.enterpriseInTrialSuffix}")
            }
        }
        AdminSheetRow(strings.appliedLimitLabel, quota.globalLimit.toString(), limitSource)
        val resetSource = if (data.accountKind == AdminQuotaAccountKind.EnterpriseDriver && data.enterpriseName != null) {
            strings.resetSourceEnterprise.fillPlaceholders("enterprise" to data.enterpriseName.orEmpty())
        } else {
            strings.resetSourceOwn
        }
        AdminSheetRow(strings.resetLabel, OptimizationQuotaFormat.fullDate(quota.resetAt), resetSource, last = true)

        Div(attrs = { style { property("margin-top", "14px"); property("padding-top", "14px"); property("border-top", "1px solid $WebSurfaceDim") } }) {
            Div(attrs = { style { property("font-size", "12.5px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "6px") } }) {
                Text(strings.replaceLabel)
            }
            Div(attrs = { style { property("display", "flex"); property("gap", "8px") } }) {
                Input(
                    type = InputType.Text,
                    attrs = {
                        attr("inputmode", "numeric")
                        attr("placeholder", quota.remaining.toString())
                        value(state.remainingInput)
                        onInput { event -> onIntent(AdminAccountQuotaIntent.UpdateRemainingInput(event.value)) }
                        style {
                            property("width", "110px"); padding(8.px, 10.px); property("border-radius", "8px"); property("font-size", "14px")
                            property(
                                "border",
                                "1.5px solid " +
                                    if (state.remainingInputInvalid || state.remainingInputTooLarge || state.replaceError != null) WebError else WebBorder,
                            )
                        }
                    },
                )
                Button(attrs = {
                    if (!state.isReplacing) onClick { onIntent(AdminAccountQuotaIntent.ReplaceRemaining) }
                    style {
                        secondaryButtonStyle(this)
                        if (state.isReplacing) { property("opacity", "0.5"); property("cursor", "not-allowed") }
                    }
                }) {
                    if (state.isReplacing) WebSpinner(size = 14, strokeWidth = 2) else Text(strings.replaceAction)
                }
            }
            val replaceError = when {
                state.remainingInputInvalid -> strings.replaceInvalid
                state.remainingInputTooLarge -> strings.replaceMaximum
                state.replaceError == "validation" -> strings.replaceRejected
                state.replaceError == "offline" -> strings.errorNoConnection
                state.replaceError != null -> strings.errorGeneric
                else -> null
            }
            if (replaceError != null) {
                Div(attrs = { style { property("font-size", "11.5px"); property("color", WebErrorInk); property("margin-top", "4px") } }) { Text(replaceError) }
            }
            Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "8px"); property("line-height", "1.45") } }) {
                val help = if (data.accountKind == AdminQuotaAccountKind.EnterpriseDriver) {
                    strings.replaceHelpEnterpriseDriver.fillPlaceholders("name" to "${data.account.firstName} ${data.account.lastName}".trim())
                } else {
                    strings.replaceHelp
                }
                // The server's cap is stated up front, not only once rejected.
                Text("$help ${strings.replaceMaximum}")
            }
        }
    }
}

private fun subscriptionLabel(kind: OptimizationSubscriptionKind, strings: OptimizationQuotaStrings) = when (kind) {
    OptimizationSubscriptionKind.Trial -> strings.subscriptionTrial
    OptimizationSubscriptionKind.Paid -> strings.subscriptionPaid
}

@Composable
internal fun AdminSheetRow(label: String, value: String, detail: String?, last: Boolean = false) {
    Div(attrs = {
        style {
            property("display", "flex"); property("justify-content", "space-between"); property("align-items", "flex-start"); property("gap", "12px")
            padding(10.px, 0.px)
            if (!last) property("border-bottom", "1px solid $WebSurfaceDim")
        }
    }) {
        Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary) } }) { Text(label) }
        Div(attrs = { style { property("text-align", "right") } }) {
            Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary) } }) { Text(value) }
            if (detail != null) {
                Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary) } }) { Text(detail) }
            }
        }
    }
}

@Composable
internal fun AdminStatusChip(label: String, fg: String, bg: String) {
    Span(attrs = {
        style {
            property("font-size", "11px"); property("font-weight", "700"); padding(3.px, 8.px)
            property("border-radius", "999px"); property("color", fg); property("background", bg)
        }
    }) { Text(label) }
}
